// client/src/components/tendermaker/BidDocPane.js
import React, { useEffect, useRef, useState } from "react";
import mammoth from "mammoth";
import { tmBidDocDownload, tmBidDocToDocx, tmProxyPdfUrl } from "./tendermakerApi";

const btn = {
  padding: "5px 10px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 11.5,
};
const btn2 = { ...btn, background: "#fafafa" };
const btnPrimary = { ...btn, background: "#2563eb", color: "#fff", borderColor: "#2563eb" };

function isPdfName(name = "") {
  return String(name).split(".").pop()?.toLowerCase() === "pdf";
}
function isDocxName(name = "") {
  return String(name).split(".").pop()?.toLowerCase() === "docx";
}

export default function BidDocPane({
  bidId,
  bidDocs,
  docsBusy,
  activeItemId,
  jumpTo,
  onSetJumpTo,
  onUploadBidDocs,
  onLoadBidDocs,
  onAddBidDocToItem,
  onAddBidDocPages,
  onGetExtractedPages,
  onSetErr,
  previewPdfUrl,
  onClearPreview,
}) {
  const bidFileRef = useRef(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [viewMode, setViewMode] = useState(""); // "pdf" | "word"
  const [pdfUrl, setPdfUrl] = useState("");
  const [wordHtml, setWordHtml] = useState("");
  const [viewBusy, setViewBusy] = useState(false);
  const [convertedWordUrls, setConvertedWordUrls] = useState({}); // docId -> url
  const [pageRange, setPageRange] = useState(""); // e.g. "1-3,5,7"

  // Handle evidence jump — open PDF at specific page
  useEffect(() => {
    if (!jumpTo?.docId) return;
    const doc = bidDocs.find((d) => d.id === jumpTo.docId);
    if (!doc) return;
    viewPdf(doc, jumpTo.page || 1);
    onSetJumpTo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpTo?.docId, jumpTo?.page]);

  async function viewPdf(doc, page) {
    setViewBusy(true);
    setViewingDoc(doc);
    setViewMode("pdf");
    setWordHtml("");
    setPageRange("");
    try {
      const r = await tmBidDocDownload(bidId, doc.id);
      if (r?.url) {
        const base = tmProxyPdfUrl(r.url);
        setPdfUrl(page ? `${base}#page=${page}` : base);
      }
    } catch (e) {
      onSetErr(String(e?.message || e));
    } finally {
      setViewBusy(false);
    }
  }

  async function viewWord(doc, docxUrl) {
    setViewBusy(true);
    setViewingDoc(doc);
    setViewMode("word");
    setPdfUrl("");
    setPageRange("");
    try {
      const url = docxUrl || (await tmBidDocDownload(bidId, doc.id))?.url;
      if (!url) throw new Error("Could not get download URL");
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);
      const ab = await resp.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer: ab });
      setWordHtml(result.value || "<p>No readable content found.</p>");
    } catch (e) {
      onSetErr(String(e?.message || e));
      setWordHtml("<p>Failed to load document.</p>");
    } finally {
      setViewBusy(false);
    }
  }

  async function convertPdfToWord(doc) {
    setViewBusy(true);
    try {
      const r = await tmBidDocToDocx(bidId, doc.id);
      if (r?.url) {
        setConvertedWordUrls((prev) => ({ ...prev, [doc.id]: r.url }));
        await viewWord(doc, r.url);
      } else {
        onSetErr("Conversion returned no URL. Ensure 'docx' npm package is installed on the server.");
      }
    } catch (e) {
      onSetErr(String(e?.message || e));
    } finally {
      setViewBusy(false);
    }
  }

  // Parse page range string: "1-3,5,7-9" → ["1-3","5","7-9"]
  function parsePageRange(str) {
    if (!str.trim()) return [];
    return str.split(",").map((s) => s.trim()).filter(Boolean);
  }

  // Add entire document to requirement
  function handleAddWholeDoc() {
    if (!viewingDoc || !activeItemId || !onAddBidDocToItem) return;
    onAddBidDocToItem(activeItemId, viewingDoc);
  }

  // Add specific pages from PDF to requirement
  function handleAddPages() {
    if (!viewingDoc || !activeItemId) return;
    const pages = parsePageRange(pageRange);
    if (!pages.length) {
      onSetErr("Enter a page range first, e.g. 1-3,5");
      return;
    }
    if (onAddBidDocPages) {
      onAddBidDocPages(activeItemId, viewingDoc, pages);
    }
  }

  async function handleDownload(doc) {
    try {
      const r = await tmBidDocDownload(bidId, doc.id);
      if (r?.url) window.open(r.url, "_blank");
    } catch (e) {
      onSetErr(String(e?.message || e));
    }
  }

  const viewingPdf = viewingDoc && isPdfName(viewingDoc.fileName || "");
  const viewingDocx = viewingDoc && isDocxName(viewingDoc.fileName || "");
  const hasConvertedWord = viewingDoc && !!convertedWordUrls[viewingDoc.id];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, height: "100%" }}>
      {/* Header + upload */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ fontWeight: 900, fontSize: 14 }}>Bid Documents</div>
        <input
          ref={bidFileRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx"
          disabled={docsBusy}
          style={{ fontSize: 12, maxWidth: 180 }}
          onChange={(e) => {
            onUploadBidDocs(e.target.files).catch((x) => onSetErr(String(x?.message || x)));
            if (bidFileRef.current) bidFileRef.current.value = "";
          }}
        />
        <button style={btn2} onClick={() => onLoadBidDocs()} disabled={docsBusy}>
          Refresh
        </button>
      </div>

      {/* Document list */}
      <div style={{ flex: "0 0 auto", maxHeight: 200, overflowY: "auto" }}>
        {bidDocs.length === 0 ? (
          <div style={{ color: "#6b7280", fontSize: 12 }}>No bid documents uploaded yet.</div>
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            {bidDocs.map((d) => {
              const fileName = d.fileName || d.id;
              const pdf = isPdfName(fileName);
              const docx = isDocxName(fileName);
              const isViewing = viewingDoc?.id === d.id;
              const hasConv = !!convertedWordUrls[d.id];

              return (
                <div
                  key={d.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 10px",
                    borderRadius: 8,
                    border: isViewing ? "1px solid #93b4f5" : "1px solid #eef2f7",
                    background: isViewing ? "#eff6ff" : "#fafafa",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 12, flex: 1, minWidth: 100 }}>
                    {fileName}
                    <span style={{
                      marginLeft: 6,
                      padding: "1px 5px",
                      borderRadius: 4,
                      background: pdf ? "#fee2e2" : docx ? "#dbeafe" : "#f3f4f6",
                      fontWeight: 700,
                      fontSize: 9,
                    }}>
                      {pdf ? "PDF" : docx ? "DOCX" : ""}
                    </span>
                  </div>

                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {pdf && (
                      <>
                        <button
                          style={
                            isViewing && viewMode === "pdf"
                              ? { ...btn, background: "#dbeafe", borderColor: "#93c5fd" }
                              : btn
                          }
                          onClick={() => viewPdf(d)}
                        >
                          View PDF
                        </button>
                        <button
                          style={{ ...btn, background: "#f0fdf4", borderColor: "#bbf7d0" }}
                          onClick={() => convertPdfToWord(d)}
                          disabled={viewBusy}
                        >
                          {hasConv ? "Reconvert" : "PDF \u2192 Word"}
                        </button>
                        {hasConv && (
                          <button
                            style={
                              isViewing && viewMode === "word"
                                ? { ...btn, background: "#dbeafe", borderColor: "#93c5fd" }
                                : btn
                            }
                            onClick={() => viewWord(d, convertedWordUrls[d.id])}
                          >
                            View Word
                          </button>
                        )}
                      </>
                    )}
                    {docx && (
                      <button
                        style={
                          isViewing && viewMode === "word"
                            ? { ...btn, background: "#dbeafe", borderColor: "#93c5fd" }
                            : btn
                        }
                        onClick={() => viewWord(d)}
                      >
                        View Word
                      </button>
                    )}
                    <button style={btn2} onClick={() => handleDownload(d)} title="Download original file">
                      &darr;
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add to Requirement Files bar */}
      {viewingDoc && activeItemId && onAddBidDocToItem && (
        <div
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            padding: "6px 10px",
            background: "#f0fdf4",
            borderRadius: 8,
            border: "1px solid #bbf7d0",
            flexWrap: "wrap",
          }}
        >
          {/* Add entire document */}
          <button style={btnPrimary} onClick={handleAddWholeDoc} disabled={viewBusy}>
            Add Entire {viewingPdf ? "PDF" : "Doc"}
          </button>

          {/* Page range for PDFs */}
          {viewingPdf && onAddBidDocPages && (
            <>
              <input
                value={pageRange}
                onChange={(e) => setPageRange(e.target.value)}
                placeholder="Pages: 1-3,5,7"
                style={{
                  padding: "4px 8px",
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  fontSize: 11,
                  width: 120,
                  fontWeight: 600,
                }}
              />
              <button
                style={{ ...btn, background: "#ecfdf5", borderColor: "#bbf7d0", fontSize: 11, padding: "4px 10px" }}
                onClick={handleAddPages}
                disabled={viewBusy || !pageRange.trim()}
              >
                Add Pages
              </button>
            </>
          )}

          <span style={{ color: "#374151", fontSize: 10, fontWeight: 600 }}>
            {viewingDoc.fileName || viewingDoc.id} → active requirement
          </span>
        </div>
      )}

      {/* Inline viewer */}
      <div style={{ flex: 1, minHeight: 0, overflow: "hidden", borderRadius: 8 }}>
        {/* Preview PDF (from branding/merge) takes priority */}
        {previewPdfUrl && (
          <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center", padding: "4px 8px", background: "#ecfdf5", borderRadius: "8px 8px 0 0", border: "1px solid #bbf7d0" }}>
              <span style={{ fontWeight: 700, fontSize: 11, color: "#166534", flex: 1 }}>Preview: Branded / Merged PDF</span>
              <button style={{ ...btn, fontSize: 10, padding: "2px 8px", background: "#fff" }} onClick={() => window.open(previewPdfUrl, "_blank")}>Download</button>
              <button style={{ ...btn, fontSize: 10, padding: "2px 8px", background: "#fff" }} onClick={onClearPreview}>Close</button>
            </div>
            <iframe
              src={previewPdfUrl}
              style={{ width: "100%", flex: 1, border: "1px solid #bbf7d0", borderRadius: "0 0 8px 8px" }}
              title="Preview PDF"
            />
          </div>
        )}

        {!previewPdfUrl && viewBusy && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#6b7280", fontSize: 12, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8 }}>
            Loading document...
          </div>
        )}

        {!previewPdfUrl && !viewBusy && viewingDoc && viewMode === "pdf" && pdfUrl && (
          <iframe
            src={pdfUrl}
            style={{ width: "100%", height: "100%", border: "1px solid #e5e7eb", borderRadius: 8 }}
            title={`PDF: ${viewingDoc.fileName || viewingDoc.id}`}
          />
        )}

        {!previewPdfUrl && !viewBusy && viewingDoc && viewMode === "word" && wordHtml && (
          <div
            style={{ height: "100%", overflowY: "auto", padding: 16, border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", fontSize: 13, lineHeight: 1.6 }}
            dangerouslySetInnerHTML={{ __html: wordHtml }}
          />
        )}

        {!previewPdfUrl && !viewBusy && !viewingDoc && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#9ca3af", fontSize: 13, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8 }}>
            Select a document above to view
          </div>
        )}
      </div>
    </div>
  );
}
